import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';

import ValueSpinner from './ValueSpinner';
import { COLORS } from './dataPlotConstants';

const normalizeLabel = (label: any) => (typeof label === 'string' ? label.toUpperCase() : label);

const StatItem = ({ value, label }: any) => (
    <div className='flex flex-1 flex-col items-center text-center'>
        <p className='text-[32px] font-semibold text-[#4E5565]'>{String(value).padStart(2, '0')}</p>
        <p className='text-sm font-semibold uppercase tracking-[0.3em] text-[#8B8E9C]'>{normalizeLabel(label)}</p>
    </div>
);

function FailDurationCard({ failDay, failHour, failMinute, isMobile, isLoading, baseTitleStyle, buildCardStyle }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Overview' });

    // The spinner sits in a box the height of the digits' line, so swapping a number
    // for it can't resize the pill.
    const digits = (value: any, color: string, size: number, boxHeight: number) =>
        isLoading ? <ValueSpinner color={color} size={size} boxHeight={boxHeight} /> : value;
    const mobileCard = (
        <div className='fail-duration-mobile'>
            <p className='fail-duration-mobile__title'>{t('failLastedTime')}</p>
            <div className='fail-duration-mobile__pill fail-duration-mobile__pill--top'>
                <span className='fail-duration-mobile__pill-label'>{t('fail')}</span>
                <div className='fail-duration-mobile__pill-day'>
                    <span className='fail-duration-mobile__pill-day-number'>
                        {digits(String(failDay).padStart(2, '0'), '#ff9090', 22, 28)}
                    </span>
                    <span className='fail-duration-mobile__pill-day-suffix'>{normalizeLabel(t('day'))}</span>
                </div>
            </div>
            <div className='fail-duration-mobile__pill fail-duration-mobile__pill--bottom'>
                <div className='fail-duration-mobile__time-block'>
                    <span className='fail-duration-mobile__time-number'>
                        {digits(String(failHour).padStart(2, '0'), COLORS.textSecondary, 18, 24)}
                    </span>
                    <span className='fail-duration-mobile__time-unit'>{normalizeLabel(t('hour'))}</span>
                </div>
                <div className='fail-duration-mobile__divider' />
                <div className='fail-duration-mobile__time-block'>
                    <span className='fail-duration-mobile__time-number'>
                        {digits(String(failMinute).padStart(2, '0'), COLORS.textSecondary, 18, 24)}
                    </span>
                    <span className='fail-duration-mobile__time-unit'>{normalizeLabel(t('minute'))}</span>
                </div>
            </div>
        </div>
    );

    if (isMobile) {
        return mobileCard;
    }

    const desktopCard = (
        <div className='card' style={buildCardStyle('small', { alignItems: 'center' })}>
            <div style={{ ...baseTitleStyle, marginBottom: '10px' }}>{t('failLastedTime')}</div>
            <div
                style={{
                    width: '250px',
                    height: '110px',
                    backgroundColor: '#FFA8A8',
                    boxShadow: 'inset 0px 0px 5px rgba(0, 0, 0, 0.25)',
                    borderRadius: '30px',
                    display: 'flex',
                    flexFlow: 'row nowrap',
                    justifyContent: 'center',
                }}
            >
                <p
                    style={{
                        width: '100px',
                        margin: '2rem 0rem 1rem 0rem',
                        color: COLORS.white,
                        textAlign: 'center',
                        fontFamily: 'Roboto',
                        fontWeight: 600,
                        fontSize: '2rem',
                        letterSpacing: '0.2em',
                    }}
                >
                    {t('fail')}
                </p>
                <div
                    style={{
                        width: '150px',
                        height: '110px',
                        backgroundColor: COLORS.white,
                        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.25)',
                        borderRadius: '30px',
                        display: 'flex',
                        flexFlow: 'row nowrap',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '8px',
                    }}
                >
                    <p
                        style={{
                            margin: '18px 0px',
                            color: '#FF3838',
                            textAlign: 'center',
                            fontFamily: 'Roboto',
                            fontWeight: 600,
                            fontSize: '64px',
                        }}
                    >
                        {digits(failDay, '#FF3838', 40, 64)}
                    </p>
                    <p
                        style={{
                            margin: '35px 0px 0px',
                            color: COLORS.text,
                            textAlign: 'center',
                            fontFamily: 'Roboto',
                            fontWeight: 600,
                            fontSize: '32px',
                        }}
                    >
                        {t('day')}
                    </p>
                </div>
            </div>
            <div
                style={{
                    width: '250px',
                    height: '110px',
                    padding: '5px',
                    backgroundColor: COLORS.white,
                    boxShadow: 'inset 0px 0px 5px rgba(0, 0, 0, 0.25)',
                    borderRadius: '30px',
                    display: 'flex',
                    flexFlow: 'row nowrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <div style={{ display: 'flex', flexFlow: 'row nowrap', justifyContent: 'space-evenly' }}>
                    <p
                        style={{
                            margin: '26px 5px',
                            color: COLORS.textSecondary,
                            textAlign: 'center',
                            fontFamily: 'Roboto',
                            fontWeight: 600,
                            fontSize: '44px',
                        }}
                    >
                        {digits(failHour, COLORS.textSecondary, 30, 44)}
                    </p>
                    <p
                        style={{
                            margin: '40px 5px',
                            color: COLORS.textSecondary,
                            textAlign: 'center',
                            fontFamily: 'Roboto',
                            fontWeight: 600,
                            fontSize: '22px',
                        }}
                    >
                        {t('hour')}
                    </p>
                </div>

                <div className='vline' />

                <div style={{ display: 'flex', flexFlow: 'row nowrap', justifyContent: 'space-evenly' }}>
                    <p
                        style={{
                            margin: '26px 5px',
                            color: COLORS.textSecondary,
                            textAlign: 'center',
                            fontFamily: 'Roboto',
                            fontWeight: 600,
                            fontSize: '44px',
                        }}
                    >
                        {digits(failMinute, COLORS.textSecondary, 30, 44)}
                    </p>
                    <p
                        style={{
                            margin: '40px 5px',
                            color: COLORS.textSecondary,
                            textAlign: 'center',
                            fontFamily: 'Roboto',
                            fontWeight: 600,
                            fontSize: '22px',
                        }}
                    >
                        {t('minute')}
                    </p>
                </div>
            </div>
        </div>
    );

    return desktopCard;
}

FailDurationCard.propTypes = {
    isLoading: PropTypes.bool,
    failDay: PropTypes.number.isRequired,
    failHour: PropTypes.number.isRequired,
    failMinute: PropTypes.number.isRequired,
    isMobile: PropTypes.bool.isRequired,
    baseTitleStyle: PropTypes.object.isRequired,
    buildCardStyle: PropTypes.func.isRequired,
};

export default FailDurationCard;
